import axios from '../axios';
import { apiErrorMessages } from '../../errors/apiErrorMessages';

// ---------------------------------------------------------------------------
// Estoque / Movements — Service Layer (handoffs 0089 + 0093)
//
// Camada fina sobre o axios singleton, padrão FC1: desembrulha o envelope
// JSend no sucesso (`data.data`) e propaga o erro cru. A tradução camelCase
// (args) → snake_case (payload/query do backend) acontece SÓ aqui.
//
// Cobre o núcleo de escrita Geração 3 (0093: POST/PUT/DELETE /movements +
// stock-impact-check) e o duplicate-check (0089). O fluxo XML
// (`POST /movements/xml`) segue shape antigo e fica fora do service até o
// backend migrá-lo.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// LEITURA (Geração 2 — REF-013-restante no backend)
//
// `POST /movement-search`, `GET /movements/:id` e `GET /movements/:id/pdf`
// seguem fora do envelope JSend: o search devolve **array cru** de headers de
// movimento e o show devolve **objeto flat** com `ItemMovements[]`. O service
// entra aqui (2026-09-12, redesenho das telas) para tirar o `axios` das
// páginas e concentrar a margem camel→snake — NÃO para fingir que o shape é
// G3. Quando o backend migrar a leitura, só estes três corpos mudam.
// ---------------------------------------------------------------------------

// Busca da listagem. Só envia filtro preenchido (navegacao.md §1.8); o
// intervalo de datas é exigido pelo backend e vai sempre.
export async function searchMovements({
   startDate,
   endDate,
   id,
   docNumber,
   pdvId,
   userId,
   moveType,
   movementFlag,
   prodSku,
}) {
   const payload = { start_date: startDate, end_date: endDate };

   const optional = {
      id,
      doc_number: docNumber,
      pdv_id: pdvId,
      user_id: userId,
      move_type: moveType,
      movement_flag: movementFlag,
      prod_sku: prodSku,
   };
   Object.entries(optional).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
         payload[key] = value;
      }
   });

   const response = await axios.post('/movement-search', payload);
   return Array.isArray(response?.data) ? response.data : [];
}

// Detalhe do movimento (shape cru G2: header flat + `ItemMovements[]` +
// `transfer` aditivo do handoff 0099). Devolve `null` só quando o corpo vem
// vazio — erro é propagado para o chamador tratar (FC4).
export async function getMovement(id) {
   const response = await axios.get(`/movements/${id}`);
   return response?.data ?? null;
}

// ⚠️ `GET /movements/:id/pdf` NÃO tem mais consumidor no front (2026-09-13):
// o comprovante passou a ser montado e impresso pelo cliente
// (`pages/MovementReceipt`), pelo diálogo do navegador. O leitor daqui foi
// removido junto com `utils/movementPdf.js` — manter um service sem chamador
// é código morto que sugere um caminho que não existe mais.
//
// A ROTA do backend segue no ar de propósito: removê-la (e com ela o
// Puppeteer por request, que vaza processo quando o template falha — risco #7
// do handoff 0090) é tarefa de uma sessão de backend, depois que a impressão
// nova estiver validada em produção.

// Warn guard de duplicidade: consulta movimentos ATIVOS já registrados com o
// mesmo quarteto de índices. Recurso informativo e não-bloqueante — a criação
// nunca é barrada por este check. Máx. 20 itens, mais recentes primeiro.
export async function checkDuplicateMovement({
   pdvId,
   docNumber,
   docType,
   moveType,
}) {
   const response = await axios.get('/movements/duplicate-check', {
      params: {
         pdv_id: Number(pdvId),
         doc_number: Number(docNumber),
         doc_type: docType,
         move_type: moveType,
      },
   });
   return response?.data?.data?.duplicates ?? [];
}

// Warn guard de saldo negativo (0093 §5a): pré-check informativo e
// não-bloqueante. Na EDIÇÃO, `movementId` faz o cálculo considerar a
// devolução do efeito atual do movimento. Vazio = sem impacto.
export async function checkStockImpact({ movementId, pdvId, moveType, items }) {
   const payload = {
      pdv_id: Number(pdvId),
      move_type: moveType,
      items: (items ?? []).map((item) => ({
         prod_id: Number(item.prod_id),
         prod_qtd: Number(item.prod_qtd),
      })),
   };
   if (movementId) payload.movement_id = Number(movementId);

   const response = await axios.post('/movements/stock-impact-check', payload);
   return response?.data?.data?.negative_stock ?? [];
}

// O contrato do 0093 declara `items: [{ prod_id, prod_qtd }]` e header com 5
// campos editáveis — as listas das telas carregam extras de UI (sku, prodName,
// user_id...) que não pertencem ao payload validado por Zod. A limpeza é da
// margem (aqui), não das páginas.
function toContractItems(items) {
   return (items ?? []).map((item) => ({
      prod_id: Number(item.prod_id),
      prod_qtd: Number(item.prod_qtd),
   }));
}

function toContractMovement(movement) {
   const header = {
      pdv_id: Number(movement.pdv_id),
      move_type: movement.move_type,
      doc_type: movement.doc_type,
      // Enviado sempre (null quando vazio): no PUT parcial, omitir deixaria a
      // descrição antiga ao limpar o campo.
      movement_description: movement.movement_description || null,
   };
   // doc_number é opcional (omitido → null no backend).
   if (movement.doc_number !== undefined && movement.doc_number !== '') {
      header.doc_number = Number(movement.doc_number);
   }
   return header;
}

// `POST /movements` (201 JSend).
export async function createMovement({ movement, items }) {
   const response = await axios.post('/movements', {
      movement: toContractMovement(movement),
      items: toContractItems(items),
   });
   const data = response?.data?.data ?? {};
   return {
      movement: data.movement ?? null,
      stockWarnings: data.stock_warnings ?? [],
      message: response?.data?.message ?? 'Movimento criado com sucesso.',
   };
}

// `PUT /movements/:id` — o editor oficial (0093 §3). `items` é a lista
// COMPLETA desejada (substituição integral, não um diff).
export async function updateMovement(id, { movement, items }) {
   const response = await axios.put(`/movements/${id}`, {
      movement: toContractMovement(movement),
      items: toContractItems(items),
   });
   const data = response?.data?.data ?? {};
   return {
      movement: data.movement ?? null,
      stockWarnings: data.stock_warnings ?? [],
      snapshotBackfill: data.snapshot_backfill ?? null,
      message: response?.data?.message ?? 'Movimento atualizado com sucesso.',
   };
}

// `DELETE /movements/:id` (200 com body, deliberado): cria movimento reverso.
// Handoff 0095: motivo obrigatório (8–255 chars após trim) — em DELETE o
// body vai no config do axios (`data`).
export async function deleteMovement(id, { cancelledReason }) {
   const response = await axios.delete(`/movements/${id}`, {
      data: { cancelled_reason: cancelledReason },
   });
   const data = response?.data?.data ?? {};
   return {
      reversionMovementId: data.reversion_movement_id ?? null,
      stockWarnings: data.stock_warnings ?? [],
      message: response?.data?.message ?? 'Movimento excluído com sucesso.',
   };
}

// ---------------------------------------------------------------------------
// Leitor defensivo de erro — local do módulo até o `ApiError` global (FC1)
// ser liberado. Aceita o envelope JSend (`errors[]` de {field,message,code}),
// o legado (`errors: string[]`) e resposta sem body; nunca interpola objeto
// como string (o clássico `[object Object]`).
//
// O `code` resolvido passa pelo dicionário único do FC4 (§B.4) antes da
// mensagem do backend — foi o que trouxe o `transfer_movement_locked` (409 do
// handoff 0099) para as telas de editar/excluir movimento sem reescrevê-las:
// movimento gerado por transferência entre PDVs só muda pelo fluxo de
// transferência.
// ---------------------------------------------------------------------------
export function readMovementApiError(error) {
   const status = error?.response?.status ?? 0;
   const body = error?.response?.data;

   const fieldErrors = {};
   let code = null;
   const messages = [];

   if (Array.isArray(body?.errors)) {
      body.errors.forEach((item) => {
         if (typeof item === 'string') {
            messages.push(item);
            return;
         }
         if (item && typeof item.message === 'string') {
            if (item.field) fieldErrors[item.field] = item.message;
            else messages.push(item.message);
            if (!code && item.code) code = item.code;
         }
      });
      if (!code && typeof body.errors[0]?.code === 'string') {
         code = body.errors[0].code;
      }
   }

   const message =
      (code ? apiErrorMessages[code] : null) ??
      body?.message ??
      (messages.length ? messages.join('; ') : null) ??
      (status
         ? 'Não foi possível concluir a operação. Tente novamente.'
         : 'Falha de conexão. Tente novamente.');

   return { status, code, message, fieldErrors };
}
